import R from 'ramda';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { deleteOfferBinding, createOfferBinding, updateOfferBindingsOrder } from 'modules/shared-menu/api';
import { SelectionCategory, SharedMenu } from 'modules/shared-menu/types';

import { useSelectionData } from '../../../../../hooks/useSelectionData';
import { flattenCategories, getOfferCategory } from '../../../utils';

type OfferOrigin = {
  categoryId: string;
  position: number;
};

const useAsyncValue = <T>(value: T) => {
  const ref = useRef<T>(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
};

const useOfferCategoryChange = () => {
  const intl = useIntl();
  const { categories, selection, updateSelectionData } = useSelectionData();

  const getOfferOrigin = (offerId: string): OfferOrigin | null => {
    const originCategory = getOfferCategory(flattenCategories(categories), offerId);
    if (!originCategory) {
      return null;
    }
    const position = (originCategory.offer_bindings ?? []).findIndex(binding => binding.offer_id === offerId);
    if (position === -1) {
      throw new Error('No binding for item found');
    }
    return { categoryId: originCategory.id, position };
  };

  const moveOfferToCategory = async (targetCategoryId: string, offerId: string): Promise<any> => {
    try {
      return await createOfferBinding(selection.id, targetCategoryId, offerId);
    } catch (_) {
      throw new Error(intl.formatMessage({ id: 'selection.moveItemBackToCategoryFailed' }));
    }
  };

  const noveOfferToPosition = async (category: SelectionCategory, position: number, offerId: string): Promise<any> => {
    const categoryOfferBindings = category.offer_bindings ?? [];
    const newOfferBindingIndex = categoryOfferBindings.findIndex(binding => binding.offer_id === offerId);
    if (newOfferBindingIndex === -1) {
      throw new Error('No binding for item found in updated menu');
    }
    const itemBindingForItem = categoryOfferBindings[newOfferBindingIndex];
    const reorderedItemBindings = R.insert(
      position,
      itemBindingForItem,
      R.remove(newOfferBindingIndex, 1, categoryOfferBindings),
    );
    try {
      return await updateOfferBindingsOrder(
        selection.id,
        category.id,
        reorderedItemBindings.map(binding => binding.offer_id),
      );
    } catch (_) {
      throw new Error(intl.formatMessage({ id: 'selection.moveItemBackToCategoryPositionFailed' }));
    }
  };

  const removeOfferFromCategory = async (offerId: string): Promise<any> => {
    const itemCategory = getOfferCategory(flattenCategories(categories), offerId);
    if (!itemCategory) {
      throw new Error('Category not found for item');
    }
    return await deleteOfferBinding(selection.id, itemCategory.id, offerId);
  };

  const undoItemCategoryChange = useAsyncValue(async (offerOrigin: OfferOrigin | null, offerId: string) => {
    if (offerOrigin) {
      let updatedSelection: SharedMenu | undefined;
      try {
        updatedSelection = await moveOfferToCategory(offerOrigin.categoryId, offerId);
        const newCategory = flattenCategories(updatedSelection?.categories ?? []).find(
          category => category.id === offerOrigin.categoryId,
        );
        if (!newCategory) {
          throw new Error('Category not found for item');
        }
        updatedSelection = await noveOfferToPosition(newCategory, offerOrigin.position, offerId);
      } finally {
        if (updatedSelection) {
          updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
        }
      }
    } else {
      const updatedSelection = await removeOfferFromCategory(offerId);
      updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
    }
  });

  /**
   * @param targetCategoryId Removes from category if null
   * @param offerId
   * @returns An async function to undo the change
   */
  const changeOfferCategory = async (targetCategoryId: string | null, offerId: string) => {
    const offerOrigin = getOfferOrigin(offerId);
    let updatedSelection: SharedMenu;
    if (targetCategoryId === null) {
      if (!offerOrigin) {
        throw new Error('Cannot remove item from category, because it does not belong to one');
      }
      updatedSelection = await removeOfferFromCategory(offerId);
    } else {
      if (offerOrigin && offerOrigin.categoryId === targetCategoryId) {
        throw new Error('Item already belongs to the category');
      }
      updatedSelection = await createOfferBinding(selection.id, targetCategoryId, offerId);
    }
    updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
    const undo = () => undoItemCategoryChange.current(offerOrigin, offerId);
    return undo;
  };

  return changeOfferCategory;
};

export default useOfferCategoryChange;
